import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { supabase } from "@/integrations/supabase/client";

const mailingListSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100, "First name must be less than 100 characters"),
  lastName: z.string().trim().min(1, "Last name is required").max(100, "Last name must be less than 100 characters"),
  email: z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters"),
  mobile: z.string().trim().min(1, "Phone number is required").max(20, "Phone number must be less than 20 characters"),
});

type MailingListFormValues = z.infer<typeof mailingListSchema>;

const MailingListSection = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  const form = useForm<MailingListFormValues>({
    resolver: zodResolver(mailingListSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
    },
  });

  const onSubmit = async (data: MailingListFormValues) => {
    setIsSubmitting(true);
    
    try {
      const { data: result, error } = await supabase.functions.invoke('mailing-list-signup', {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phoneNumber: data.mobile,
        },
      });

      if (error) {
        throw error;
      }

      if (result?.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
        return;
      }

      // Navigate to confirmation page
      navigate('/mailing-list-confirmed');
    } catch (error) {
      console.error('Error saving to mailing list:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="mailing-list" className="py-16 md:py-20 bg-background scroll-mt-20">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-[28px] md:text-[32px] font-bold mb-4 tracking-[0.02em] uppercase text-foreground">
              STAY IN THE LOOP
            </h2>
            <p className="text-lg font-medium text-muted-foreground">
              Get exclusive presale codes, new music, and behind-the-scenes content.
            </p>
            {/* Juke Joint Teaser */}
            <div className="mt-4 inline-flex items-center gap-2 text-primary/80 hover:text-primary transition-colors cursor-pointer" onClick={() => navigate('/division-street')}>
              <span className="text-xl">🎵</span>
              <span className="text-sm font-medium">Listen Now at Sunny's Juke Joint</span>
              <span className="text-xl">🎵</span>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-medium tracking-[0.02em] uppercase text-sm">
                        First Name *
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="First" {...field} className="bg-card border-border text-foreground" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-medium tracking-[0.02em] uppercase text-sm">
                        Last Name *
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Last" {...field} className="bg-card border-border text-foreground" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium tracking-[0.02em] uppercase text-sm">
                      Email *
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="first.last@example.com" {...field} className="bg-card border-border text-foreground" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium tracking-[0.02em] uppercase text-sm">
                      Phone Number *
                    </FormLabel>
                    <FormControl>
                      <PhoneInput 
                        value={field.value} 
                        onChange={field.onChange} 
                        className="bg-card border-border text-foreground" 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-card hover:bg-card/90 text-foreground font-bold tracking-[0.03em] uppercase text-[15px]"
                disabled={isSubmitting}
              >
                {isSubmitting ? "JOINING..." : "JOIN"}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </section>
  );
};

export default MailingListSection;
